import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowDown, BadgeCheck } from "lucide-react";
import { WA_DEMO, TRUST_LINES } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";
import { MaskedLine, Reveal } from "./Reveal";
import { Magnetic } from "./Magnetic";
import { Sparkle } from "./Sparkle";

const Shard = () => (
  <div className="shard-stage w-40 h-56 sm:w-52 sm:h-72" aria-hidden="true">
    <div className="shard">
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <div key={deg} className="shard-face" style={{ transform: `rotateY(${deg}deg) translateZ(66px)` }} />
      ))}
    </div>
  </div>
);

const UiFrame = () => (
  <div data-testid="hero-ui-frame" className="w-64 sm:w-96 bg-ink border border-brand/25 shadow-2xl shadow-black/70 ring-1 ring-white/10">
    <div className="flex items-center gap-1.5 px-3 py-2 border-b hairline bg-panel">
      <span className="w-2 h-2 rounded-full bg-brand" />
      <span className="w-2 h-2 rounded-full bg-white/20" />
      <span className="w-2 h-2 rounded-full bg-white/20" />
      <span className="ml-2 h-4 flex-1 min-w-0 bg-white/5 rounded-full font-mono text-[8px] tracking-wide text-mute flex items-center px-2 truncate">
        rishabhcreatives.com
      </span>
    </div>
    <img
      src="/assets/hero-ui.jpg"
      alt="Live portfolio website for content creator Rishabh Kumar, designed and developed by STUZIO. EDITFLOW"
      className="w-full block"
      loading="eager"
    />
  </div>
);

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const shardX = useTransform(sx, [-0.5, 0.5], [-26, 26]);
  const shardY = useTransform(sy, [-0.5, 0.5], [-18, 18]);
  const frameX = useTransform(sx, [-0.5, 0.5], [22, -22]);
  const frameY = useTransform(sy, [-0.5, 0.5], [14, -14]);

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
      aria-label="Hero"
    >
      <div className="glow-orb w-[700px] h-[700px] -top-40 -left-40 opacity-60" />
      <div className="glow-orb w-[500px] h-[500px] top-1/3 -right-32 opacity-35" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-14 lg:gap-6 items-center">
        <div className="lg:col-span-7 relative z-10">
          <Reveal y={16}>
            <p className="eyebrow mb-6 flex items-center gap-3" data-testid="hero-eyebrow">
              <span className="inline-block w-8 h-px bg-brand" />
              Web Design &amp; Development — Varanasi, India
            </p>
          </Reveal>

          <h1 className="font-display font-light tracking-tight text-white leading-[0.96] text-[13.5vw] sm:text-7xl lg:text-[5.6rem] xl:text-[6.4rem]" data-testid="hero-headline">
            <MaskedLine delay={0.1}>Smarter</MaskedLine>
            <MaskedLine delay={0.22}>Marketing.</MaskedLine>
            <MaskedLine delay={0.34}>
              Better <em className="text-shimmer not-italic font-display italic">Results.</em>
            </MaskedLine>
          </h1>

          <Reveal delay={0.5} y={26}>
            <p className="mt-7 max-w-xl text-mute text-base sm:text-lg leading-relaxed" data-testid="hero-subhead">
              We build websites that win you customers. Your free demo comes first —
              built with <span className="text-white font-medium">your real business info</span> — and you pay only when you love it.
            </p>
          </Reveal>

          <Reveal delay={0.62} y={26}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Magnetic>
                <a
                  data-testid="hero-free-demo-button"
                  href={WA_DEMO}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-lime inline-flex items-center gap-3 pl-7 pr-2 py-2 font-semibold text-sm sm:text-base tracking-wide"
                >
                  Get a Free Demo
                  <span className="btn-arrow-box !border-ink/30 !text-ink"><ArrowUpRight size={16} /></span>
                </a>
              </Magnetic>
              <Magnetic>
                <button
                  data-testid="hero-see-work-button"
                  onClick={() => scrollToId("#work")}
                  className="btn-ghost inline-flex items-center gap-3 pl-7 pr-2 py-2 text-white font-semibold text-sm sm:text-base tracking-wide"
                >
                  See Our Work
                  <span className="btn-arrow-box"><ArrowUpRight size={16} /></span>
                </button>
              </Magnetic>
            </div>
          </Reveal>

          <Reveal delay={0.74} y={20}>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-trust-lines">
              {TRUST_LINES.map((t) => (
                <span key={t} className="inline-flex items-center gap-2 text-xs sm:text-sm text-mute">
                  <BadgeCheck size={15} className="text-brand" /> {t}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.85} y={16}>
            <p className="mt-5 font-mono text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-mute" data-testid="hero-global-line">
              Trusted by clients in <span className="text-brand">India · UAE · Canada · Kenya · Australia</span>
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-5 relative h-[440px] sm:h-[520px]" data-testid="hero-visual">
          <motion.div style={reduce ? {} : { x: shardX, y: shardY }} className="absolute left-2 sm:left-6 top-2">
            <Shard />
          </motion.div>

          <motion.div style={reduce ? {} : { x: frameX, y: frameY }} className="absolute right-0 sm:right-2 bottom-0 sm:bottom-6 sparkle-float">
            <UiFrame />
          </motion.div>

          <Sparkle className="absolute top-0 right-1/3" size={20} />
          <Sparkle className="absolute bottom-16 right-4" size={14} fast />
          <Sparkle className="absolute top-1/2 left-0" size={12} />
        </div>
      </div>

      <button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#work")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-mute hover:text-brand transition-colors duration-300"
        aria-label="Scroll to work"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </button>
    </section>
  );
};
